import React, {useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import OutboundLink from '../OutboundLink'
import {OverlayTrigger, Popover, PopoverBody} from 'react-bootstrap'
import Checkbox from '../controls/Checkbox'
import {elideString} from '../../lib/utils'
import _ from 'lodash'
import EvidenceChainEdge from './EvidenceChainEdge'

// Maximum datapoints to display for a single dataset
const DP_SELECTION_LIMIT_SINGLE = 4; // Max datapoints for single dataset

/**
 * Formats a datapoint title with optional count suffix
 * @param {Object} dp - Datapoint object with title and count properties
 * @returns {string} Formatted title with count if > 1 (e.g., "Score 1.00 (sim) (3)")
 */
const dpTitle = (dp) => {
  const count = dp.count > 1 ? ` (${dp.count})` : "";
  return dp.title ? `${dp.title}${count}` :  "[title missing]";
}


const GroupedEdges = (props) => {

  const {
    edges,
    datasets,
    highlightedDatasets = null,
    actions: {isResizing = false, isDragging = false, isScrolling = false, isCarouseling = false, onComparisonClick = null} = {},
    scaleFactor = null,
    edgeIndex = null,
    hasSimilarity = false,
    edgeSpan = 1,
    edgePosition = 'center',
  } = props;

  const highlighted = new Set(highlightedDatasets);

  /**
   * Initializes which datapoints should be displayed by default
   *
   * DISPLAY RULES:
   * - Single dataset: Show up to 4 datapoints (will be split across 2 columns if >2)
   * - Multiple datasets: Show up to 2 datapoints per dataset
   *
   * This ensures each dataset block (column) gets appropriate representation
   * without overwhelming the display.
   *
   * @param {Array} edges - Array of edge objects with dataset and dataPoints
   * @returns {Array} Array of selected datapoint indices (keys)
   */
  const initializeSelectedIndices = (edges) => {
    // Group datapoints by dataset
    const byDataset = _(edges).groupBy('dataset').value();
    const datasetNames = Object.keys(byDataset);
    const selectedIndices = [];

    if (datasetNames.length === 1) {
      // Single dataset: show up to 4 datapoints (will be split across 2 columns if >2)
      const datasetPoints = byDataset[datasetNames[0]];
      const selected = _(datasetPoints)
        .take(DP_SELECTION_LIMIT_SINGLE)
        .map(x => x.key)
        .value();
      selectedIndices.push(...selected);
    } else {
      // Multiple datasets: show up to 2 datapoints per dataset block
      for (const dataset of datasetNames) {
        const datasetPoints = byDataset[dataset];
        const selected = _(datasetPoints)
          .take(2)  // Max 2 datapoints per dataset block
          .map(x => x.key)
          .value();
        selectedIndices.push(...selected);
      }
    }

    return selectedIndices;
  };

  /**
   * Processes and groups datapoints for display
   *
   * PROCESSING STEPS:
   * 1. Groups identical datapoints (same title, description, URL) and adds count
   * 2. Sorts datapoints by title within each dataset
   * 3. Flattens into single array with dataset association
   * 4. Assigns unique keys for React rendering
   * 5. Sorts by count (descending) to show most common datapoints first
   *
   * @param {Array} edges - Array of edge objects with dataset and dataPoints
   * @returns {Array} Processed datapoints with keys, counts, and dataset associations
   */
  const computeDatapoints = (edges) =>_(edges)
    .map(edge => ({
      dataset: edge.dataset,
      // Group identical datapoints (title+description+url) and add a count to the label
      dataPoints: _(edge.dataPoints)
        .groupBy(item => `${item['title']}|${item['description']}|${item['url']}`)
        .entries()
        .map(([key, groupEntries]) => ({
          ...groupEntries[0],
          title: key.split('|')[0],
          description: key.split('|')[1],
          url: key.split('|')[2],
          count: groupEntries.length,
        }))
        .sort((a, b) => a.title.toLocaleLowerCase().localeCompare(b.title.toLocaleLowerCase()))
        .value()
    }))
    .flatMap(group => group.dataPoints.map(dp => ({dataset: group.dataset, ...dp})))
    .map((el, idx) => ({key: idx, ...el}))
    .sort((a, b) => b.count - a.count)
    .value();

  const datapoints = useMemo(() => {
    return computeDatapoints(edges);
  }, [edges]);
  const perDatasetCounts = Object.fromEntries(edges.map(el => ([el.dataset, el.dataPoints.length])));

  const [selectedIndices, setSelectedIndices] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // Update selected indices when datapoints change
  useEffect(() => {
    setSelectedIndices(initializeSelectedIndices(datapoints));
  }, [datapoints]);

  const toggleSelection = (key) =>
    setSelectedIndices(selectedIndices.includes(key)
                         ? _(selectedIndices).without(key).value()
                         : [...selectedIndices, key])

  const renderAllDatapoints = () => {
    const groups = getGroupedDatapoints(false);

    return <Popover id="renderAllDatapoint"
                    className="ev-ch-pop-all-dp"
                    onScroll={(e) => e.stopPropagation()}>
      <PopoverBody className="ev-ch-pop-all-dp-body">
        {
          groups.map((group, groupidx) => (
            <ul key={groupidx}>
              <OutboundLink href={group.url}
                            title={group.description}>{`${group.title} (${group.dpCount})`}</OutboundLink>
             {
               group.dps.map((dp, dpidx) => {
                 const isChecked = selectedIndices.includes(dp.key)
                 const title = 'Select datapoints to display'
                 return (
                   <li key={dpidx}>
                     <Checkbox checked={isChecked}
                               title={title}
                               onChange={() => toggleSelection(dp.key)}/>
                     <OutboundLink href={dp.url || ""}
                                   title={`${dpTitle(dp)}\n${dp.description}`}>
                       {elideString(dpTitle(dp), 30)}
                                    < /OutboundLink>
                   </li>);
                 }
               )
             }
           </ul>
          ))
        }
      </PopoverBody>
    </Popover>
  };

  /**
   * Groups datapoints by dataset for rendering as dataset blocks
   *
   * GROUPING LOGIC:
   * - Filters datapoints based on selected indices (if selectedOnly=true)
   * - Groups datapoints by dataset
   * - Enriches with dataset metadata (name, description, URL)
   * - Each group becomes a dataset block with its datapoints
   *
   * DATASET BLOCK STRUCTURE:
   * - Dataset title/link at top
   * - List of datapoints below
   * - Datapoints may span multiple columns if single dataset with >2 datapoints
   *
   * @param {boolean} selectedOnly - If true, only include selected datapoints
   * @returns {Array} Array of dataset groups with their datapoints
   */
  const getGroupedDatapoints = (selectedOnly = false) => {
    return datapoints
      .filter(dp => selectedOnly ? selectedIndices.includes(dp.key) : true)
      .reduce((array, dp) => {
        const existingDataset = array.find(el => el.dataset === dp.dataset)
        if (existingDataset) {
          // Add datapoint to existing dataset group
          existingDataset.dps.push(dp);
        }
        else {
          // Create new dataset group
          const ds = dp.dataset === "missing" ? {name: "Missing", id: "missing"} : datasets[dp.dataset];
          if (!ds) {
            console.warn(`Dataset metadata missing for '${dp.dataset}'`);
          }
          else if (dp.dataset !== "missing" && !ds.url) {
            console.warn(`Dataset ${ds.name} is missing its URL`);
          }
          const fpInfo = ds?.fingerprints?.[dp.type];
          array.push({
                       dataset: ds?.id,
                       title: fpInfo ? `${ds.name} - ${fpInfo.name}` : ds?.name,
                       description: fpInfo?.description || ds?.description,
                       url: ds?.url,
                       dps: [dp],
                       dpCount: ds?.id ? perDatasetCounts[ds.id] : 0,
          })
        }
        return array
      }, []);
  };

  const renderSelectedDatapoints = (groupedDatapoints) => {
    return <div className="ev-ch-edge-list-wrp">
      {
        groupedDatapoints.map((group, idx) => group.dps.length && (
          <ul className="ev-ch-edge-list" key={idx}>
            {group.url
              ? (<OutboundLink
                className={`dataset-info${highlighted.has(group.dataset) ? " highlight" : ""}`}
                href={group.url} title={group.description}>
                {`${group.title} (${group.dpCount})`}
              </OutboundLink>)
              : (<span className="dataset-info" title={group.description}>{group.title}</span>)}
            {
              group.dps.map((dp, dpidx) => (
                <li key={dpidx} className="datapoints-info truncate-overflow truncate-lines-2">
                  {dp.url
                    ? (<OutboundLink href={dp.url} title={`${dpTitle(dp)}\n${dp.description}`}>{dpTitle(dp)}</OutboundLink>)
                    : (<span title={`${dpTitle(dp)}\n${dp.description} (${dp.source_id})`}>{dpTitle(dp)}</span>)}
                </li>
              ))
            }
          </ul>
        )) || (<span className="alert-warning">Evidence temporarily unavailable</span>)
      }
    </div>
  };

  const renderSummary = () => {
    const {edges = []} = props;
    const datasetCount = edges.length;
    const datapointCount = edges
      .map(edge => edge.dataPoints.length)
      .reduce((acc, count) => acc + count, 0);

    return (<div className="ev-ch-edge-summary">
      <b>{datasetCount}</b><span>{`DATA SET${datasetCount === 1 ? "" : "S"}`}</span>
      <br/>
      <b>{datapointCount}</b><span>{`DATA POINT${datapointCount === 1 ? "" : "S"}`}</span>
    </div>)
  };

  useEffect(() => {
    if (showPopup) {
      setShowPopup(false);
    }
  }, [isResizing, isDragging, isScrolling, isCarouseling, scaleFactor]);

  const handleCompareClick = () => {
    if (onComparisonClick && edgeIndex !== null) {
      onComparisonClick(edgeIndex);
    }
  };

  const small = scaleFactor === "sm";
  // Show "Show all" if there are more unique datapoints than selected OR if any have duplicates
  const hasHiddenDuplicates = datapoints.some(dp => (dp.count || 1) > 1);
  const renderExpandButton = small || datapoints.length > selectedIndices.length || hasHiddenDuplicates;

  // Determine justification based on edge position
  // Left edge: justify to the right (flex-end) toward center
  // Right edge: justify to the left (flex-start) toward center
  // Center edge: stay centered
  const justifyContent = edgePosition === 'left' ? 'flex-end' : edgePosition === 'right' ? 'flex-start' : 'center';

  return (
    <div
      className={`ev-ch-resp-edge ev-ch-resp-body-${scaleFactor}`}
      style={{gridColumn: `span ${edgeSpan}`, display: 'flex', flexDirection: 'row', justifyContent}}
    >
      {/* Inner wrapper groups all content, which moves as a unit but keeps internal centering */}
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '150px'}}>
        {/* Summary and arrow */}
        <div key="summary-with-arrow" className="ev-ch-summary-arrow-wrp">
          <div key="summary" className="ev-ch-fgm-wrp">
            {!small && renderSummary()}
            <div key="arrow" className="ev-ch-fgm-edge-body">
              <div className="ev-ch-fgm-edge-seprt"/>
              <span className="glyphicon glyphicon-play ev-ch-fgm-edge-icon"/>
            </div>
          </div>
        </div>

        {/* Dataset blocks */}
        {!small && <EvidenceChainEdge
          key="edges-details"
          groupedDatapoints={getGroupedDatapoints(true)}
          highlighted={highlighted}
          hasSimilarity={hasSimilarity}
          onCompareClick={handleCompareClick}
        />}

        {/* Show all button */}
        {renderExpandButton && <div key="show-all" className="ev-ch-edge-tgl-wrp">
        <OverlayTrigger
          key="trigger"
          defaultShow={false}
          delay={0.2}
          flip={false}
          popperConfig={{}}
          target={null}
          trigger={"click"}
          rootClose={true}
          show={showPopup}
          onToggle={() => setShowPopup(!showPopup)}
          onHide={() => setShowPopup(false)}
          placement="bottom"
          overlay={renderAllDatapoints()}>
          <span className="ev-ch-edge-tgl">
            {small
              ? <span className="glyphicon glyphicon-option-horizontal ellipsis-icon"/>
              : "Show all"}
          </span>
        </OverlayTrigger>
      </div>}
      </div>
    </div>
  )
};

GroupedEdges.propTypes = {
  edges: PropTypes.arrayOf(PropTypes.shape({
    dataset: PropTypes.string.isRequired,
    dataPoints: PropTypes.arrayOf(PropTypes.shape({
      source_id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      dataset: PropTypes.string,
      dataset_id: PropTypes.string,
    })).isRequired
  })).isRequired,
  scaleFactor: PropTypes.string.isRequired
}

export default GroupedEdges
